import React from "react";
import { Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { TableHeader } from "@services/ui-components";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import BookingHistoryDataItem from "./components/BookingHistoryDataItem";
import { formatDate, getName, sortFields } from "./utils/utils";
import { useReactiveVar } from "@apollo/client";
import {
    useGetCurrencySignQuery,
    useGetHistoryAppointmentQuery,
} from "@me-team/host/main/appointment/graphql/appointment.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";

const HistoryChangeAppointment: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);

    const { bookingId } = useParams();
    const { data, loading } = useGetHistoryAppointmentQuery({
        variables: { id: bookingId, idBranch: +branchId, companyId: +currentCompanyId },
        skip: !branchId || !bookingId,
    });
    const { data: currency, loading: currencyLoading } = useGetCurrencySignQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
    });

    const isOnlyStatusChanged = (dataArray: any[]) => {
        if (!Array.isArray(dataArray) || dataArray.length === 0) return false;
        return dataArray.every((item) => item?.field === "status");
    };

    const appointment = data?.user?.company?.branchesPaginated?.branches?.[0]?.bookings?.bookings?.[0]?.bookingHistories
        ?.slice()
        .reverse();
    const currencySign = currency?.user?.company?.currency?.currencySign;

    if (loading || currencyLoading) return <Spinner />;
    return (
        <>
            <VioletBadge
                text={t(
                    "This page displays the history of all changes to the appointment, including the date, time, user who made the changes, and details of the modifications."
                )}
            />
            <TableHeader
                title={t("History of Changes")}
                paths={[
                    { path: `/logbook/journal/${branchId}`, label: t("Logbook") },
                    { path: `/logbook/edit-record/${bookingId}/${branchId}`, label: t("Edit Record") },
                    {
                        path: `/logbook/edit-record/${bookingId}/change-history/${branchId}`,
                        label: t("History of Changes"),
                    },
                ]}
                showFilters={false}
            />

            <Table responsive>
                <tbody>
                    {appointment
                        ?.filter((i) => Array.isArray(i?.bookingHistoryData) && i.bookingHistoryData.length > 0)
                        .map((i, index, arr) => {
                            const previousHistory = arr[index + 1];
                            const previousStatusData = previousHistory?.bookingHistoryData?.find(
                                (d) => d.field === "status"
                            );
                            const previousStatus = previousStatusData?.value;
                            return (
                                <tr key={i?.id}>
                                    <td>
                                        <div className="fs-7">
                                            {getName(i)}
                                            {" - "}
                                            <span className="text-grey">
                                                {index === arr.length - 1
                                                    ? t("Create Record")
                                                    : isOnlyStatusChanged(i?.bookingHistoryData)
                                                      ? t("Change Status")
                                                      : t("Edit Record")}
                                            </span>
                                            {" - "}
                                            {formatDate(i?.changeTime, i18n.language, "datetime")}
                                        </div>
                                    </td>
                                    <td>
                                        {i?.bookingHistoryData
                                            ?.slice()
                                            .sort(sortFields)
                                            .map((data, idx) => (
                                                <BookingHistoryDataItem
                                                    key={idx}
                                                    data={data}
                                                    currency={currencySign}
                                                    historyDataArray={i?.bookingHistoryData}
                                                    isCreate={index === arr.length - 1}
                                                    previousStatus={previousStatus ? +previousStatus : null}
                                                    startTime={i?.booking?.time}
                                                />
                                            ))}
                                    </td>
                                </tr>
                            );
                        })}
                </tbody>
            </Table>
        </>
    );
};

export default HistoryChangeAppointment;
